"use client";
import { createContext, useContext, useId, type ReactNode } from "react";
import {
  resolveMenuStyle,
  menuRoles,
  roleChain,
  type MenuFormatting,
  type MenuRole,
  type MenuTextStyle,
} from "@/cms/menu-formatting";
import {
  richTextFontPresets,
  richTextFontVariable,
} from "@/utils/rich-text-fonts";

const Context = createContext<string | undefined>(undefined);
const quote = (value: string) => JSON.stringify(value).replace(/</g, "\\3c ");
const properties = {
  fontSize: "font-size",
  fontWeight: "font-weight",
  fontStyle: "font-style",
  textTransform: "text-transform",
  lineHeight: "line-height",
  letterSpacing: "letter-spacing",
  textAlign: "text-align",
  color: "color",
  backgroundColor: "background-color",
  textDecoration: "text-decoration",
} as const;
export function menuStyleCss(style: MenuTextStyle) {
  const declarations: string[] = [];
  for (const [key, property] of Object.entries(properties)) {
    const value = style[key as keyof MenuTextStyle];
    if (value !== undefined)
      declarations.push(
        `${property}:${value}${["fontSize", "letterSpacing", "gap"].includes(key) ? "px" : ""}`,
      );
  }
  if (style.fontFamily === "body" || style.fontFamily === "heading")
    declarations.push(`font-family:var(--${style.fontFamily}-font-family)`);
  else if (style.fontFamily && style.fontFamily in richTextFontPresets)
    declarations.push(
      `font-family:var(${richTextFontVariable(style.fontFamily)})`,
    );
  if (style.textAlign)
    declarations.push(
      `justify-content:${style.textAlign === "center" ? "center" : style.textAlign === "right" ? "flex-end" : "flex-start"}`,
    );
  if (style.paddingX !== undefined)
    declarations.push(`padding-inline:${style.paddingX}px`);
  if (style.paddingY !== undefined)
    declarations.push(`padding-block:${style.paddingY}px`);
  if (style.indent !== undefined)
    declarations.push(`margin-inline-start:${style.indent}px`);
  if (style.separatorColor)
    declarations.push(`border-bottom:1px solid ${style.separatorColor}`);
  return declarations.join(";");
}
function rules(selector: string, style: MenuTextStyle) {
  const css = [`${selector}{${menuStyleCss(style)}}`];
  // The label has a legacy bold default. Only override its weight when explicitly set;
  // padding/background belong on the link, never a second time on its text span.
  if (style.fontWeight !== undefined)
    css.push(`${selector} .re-nav-mega-label{font-weight:inherit}`);
  for (const [state, match] of [
    ["hover", ":is(:hover,:focus-visible)"],
    ["active", '[aria-current="page"]'],
    ["expanded", '[aria-expanded="true"]'],
  ] as const) {
    const color = style[`${state}Color`];
    const bg = style[`${state}BackgroundColor`];
    if (color || bg)
      css.push(
        `${selector}${match}{${color ? `color:${color};` : ""}${bg ? `background-color:${bg};` : ""}}`,
      );
  }
  if (style.hoverEffect) {
    css.push(
      `${selector}{position:relative}${selector}::before{content:none!important}${selector}:is(:hover,:focus-visible){text-decoration:${style.hoverEffect === "underline" ? "underline" : "none"};text-underline-offset:.25em}`,
    );
    if (style.hoverEffect === "chevron")
      css.push(
        `${selector}{padding-inline-start:1.4em}${selector}::before{content:""!important;position:absolute;left:.25em;top:50%;width:.45em;height:.45em;border-top:1px solid currentColor;border-right:1px solid currentColor;opacity:0;transform:translateY(-50%) rotate(45deg)}${selector}:is(:hover,:focus-visible)::before{opacity:1}`,
      );
    if (style.hoverEffect === "background")
      css.push(
        `${selector}:is(:hover,:focus-visible){background-color:${style.hoverBackgroundColor ?? "color-mix(in srgb,currentColor 10%,transparent)"}}`,
      );
  }
  return css.join("\n");
}
export function navigationFormattingCss(
  scope: string,
  website?: MenuFormatting,
  menu?: MenuFormatting,
  references = false,
) {
  const generate = (device: "desktop" | "mobile") =>
    menuRoles
      .flatMap((role) => {
        const root = `[data-menu-scope=${quote(scope)}][data-menu-role=${quote(role)}][data-menu-scope][data-menu-role]`;
        const resolved = resolveMenuStyle(
          website,
          menu,
          role,
          undefined,
          device,
          undefined,
          references,
        );
        const listSelector = `[data-menu-scope=${quote(scope)}][data-menu-layout-role=${quote(role)}]`;
        const layout =
          resolved.gap !== undefined
            ? `${listSelector}{gap:${resolved.gap}px;--re-nav-gap:${resolved.gap}px}${role === "mega" ? `${listSelector}>.re-nav-mega-link{margin:0}` : ""}${role === "panel" ? `${listSelector}.re-nav-panel-list>:is(.re-nav-panel-group,.re-nav-panel-stack){margin-block-start:0}` : ""}`
            : "";
        const after = (selector: string, gap: number, axis = "block") =>
          `${selector}{margin-${axis}-end:calc(${gap}px - var(--re-nav-gap,0px))}${selector}:last-child{margin-${axis}-end:0}${selector}+:is(.re-nav-panel-group,.re-nav-panel-stack){margin-block-start:0}`;
        const groupSpacing = (entry: string, gap: number) => [
          after(
            `:is(.re-nav-panel-group,.re-nav-panel-stack):has(> ${entry})`,
            gap,
          ),
          after(`.re-nav-panel-column-group:has(> ${entry})`, gap, "inline"),
        ];
        const ownRoleGap = roleChain(role)
          .filter((key) => key !== "panel")
          .some((key) =>
            [website?.roles?.[key], menu?.roles?.[key]].some(
              (style) =>
                style?.base?.gap !== undefined ||
                style?.[device]?.gap !== undefined,
            ),
          );
        const roleSpacing =
          resolved.gap !== undefined && ownRoleGap
            ? role === "parent" || role === "group"
              ? groupSpacing(root, resolved.gap)
              : role === "heading"
                ? [after(`p${root}`, resolved.gap)]
                : []
            : [];
        const itemGaps = Object.keys(menu?.items ?? {}).flatMap((id) => {
          const item = menu!.items![id];
          const ownGap = item[device]?.gap ?? item.base?.gap;
          if (
            ownGap === undefined ||
            ![
              "bar",
              "panel",
              "parent",
              "group",
              "child",
              "grandchild",
              "heading",
            ].includes(role)
          )
            return [];
          const gap = resolveMenuStyle(
            website,
            menu,
            role,
            id,
            device,
            undefined,
            references,
          ).gap;
          if (gap === undefined) return [];
          const entry = `${root}[data-menu-item=${quote(id)}]`;
          if (role === "bar")
            return [
              `li.re-nav-bar-item:has(> ${entry}){--re-nav-item-gap:${gap}px;margin-inline-end:calc(${gap}px - var(--re-nav-gap,28px))}`,
            ];
          return role === "parent" || role === "group"
            ? groupSpacing(entry, gap)
            : [after(`:is(a,button,p)${entry}`, gap)];
        });
        const barSpacing =
          role === "bar" && (resolved.gap !== undefined || itemGaps.length)
            ? `${listSelector}{justify-content:flex-start;flex:0 1 auto}${listSelector}>.re-nav-bar-item:not([data-overflow]):nth-last-child(1 of :not([data-overflow])){margin-inline-end:0!important}`
            : "";
        const surfaces =
          role === "child" || role === "grandchild"
            ? (["dropdown", "mega"] as const).flatMap((surface) => {
                const selector = `${root}[data-menu-surface=${quote(surface)}]`;
                return [
                  rules(
                    selector,
                    resolveMenuStyle(
                      website,
                      menu,
                      role,
                      undefined,
                      device,
                      surface,
                      references,
                    ),
                  ),
                  ...Object.keys(menu?.items ?? {}).map((id) =>
                    rules(
                      `${selector}[data-menu-item=${quote(id)}]`,
                      resolveMenuStyle(
                        website,
                        menu,
                        role,
                        id,
                        device,
                        surface,
                        references,
                      ),
                    ),
                  ),
                ];
              })
            : [];
        return [
          layout,
          ...roleSpacing,
          barSpacing,
          ...itemGaps,
          rules(root, resolved),
          ...surfaces,
          ...Object.keys(menu?.items ?? {}).map((id) =>
            rules(
              `${root}[data-menu-item=${quote(id)}]`,
              resolveMenuStyle(
                website,
                menu,
                role,
                id,
                device,
                undefined,
                references,
              ),
            ),
          ),
        ];
      })
      .join("\n");
  return `${generate("mobile")}\n@media(min-width:768px){${generate("desktop")}}`;
}
export function NavigationFormatting({
  website,
  menu,
  references,
  children,
}: {
  website?: MenuFormatting;
  menu?: MenuFormatting;
  references?: boolean;
  children: ReactNode;
}) {
  const scope = useId();
  if (!website && !menu) return <>{children}</>;
  return (
    <Context.Provider value={scope}>
      <style>{navigationFormattingCss(scope, website, menu, references)}</style>
      {children}
    </Context.Provider>
  );
}
export function useMenuFormatting() {
  const scope = useContext(Context);
  return (role: MenuRole, itemId?: string, surface?: "dropdown" | "mega") =>
    scope
      ? {
          "data-menu-scope": scope,
          "data-menu-role": role,
          "data-menu-item": itemId,
          "data-menu-surface": surface,
        }
      : {};
}

export function useMenuLayout() {
  const scope = useContext(Context);
  return (role: MenuRole) =>
    scope ? { "data-menu-scope": scope, "data-menu-layout-role": role } : {};
}
